/**
 * `RuntimeState.world` — flags, inventory, and the deltas the player has made
 * to authored rooms (architecture.md §3.2).
 *
 * @remarks
 * v0 field depth: flag values, inventory, removed room items, unlocked exits.
 * Item states and puzzle states join the shape with their phases as added
 * fields — and, D234, the body's: what is left of the player's health and
 * of every foe's, and which bodies have been struck. The world sub-state stores DELTAS from the authored pack (what was
 * taken, what was unlocked) — never copies of authored content, which stays
 * derivable from the bundle ("no derived data" invariant, §3.2).
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { actorIdSchema, counterIdSchema, exitIdSchema, flagIdSchema, itemIdSchema, roomIdSchema } from '../identifiers.js';
import { nonNegativeIntegerSchema } from '../primitives.js';

/**
 * One acquisition: where and when an item FIRST entered the inventory (R25).
 * Written once, never rewritten — "found here" is history and never becomes
 * wrong. Four readers: the map's found-here markers, the journal, the export,
 * and `drop` (which cannot exist without it).
 */
export const acquisitionSchema = z
	.strictObject({
		itemId: itemIdSchema,
		roomId: roomIdSchema,
		turnCount: nonNegativeIntegerSchema,
	})
	.readonly();

/** One acquisition — see {@link acquisitionSchema}. */
export type Acquisition = z.infer<typeof acquisitionSchema>;

/** Flag values, inventory membership, removed room items, unlocked exits, and container state. */
export const worldStateSchema = z
	.strictObject({
		/** R25: first-take history, append-only in take order (see {@link acquisitionSchema}). */
		acquisitions: z.array(acquisitionSchema).readonly(),
		/*
		 * D232: where each actor is NOW, when that differs from where the pack
		 * put it — a delta like every other field here (the "no derived data"
		 * rule): an actor absent from this record stands at its declared
		 * `initialRoomId`, or offstage when it declares none. Round A writes
		 * nothing into it (nothing moves yet); it is the shape round C's
		 * `move_actor` writes, `'offstage'` included, so a body can leave the
		 * stage as well as arrive. The world's for the checkpoint (D142): a
		 * restore puts every body back where the photograph had it.
		 */
		/*
		 * D234: what is LEFT of each foe's health, when that differs from what
		 * the pack declared — a delta like `actorLocations`: a body absent from
		 * this record is whole. Zero is defeated; the body stays where it
		 * fell, referable, never hostile or blocking again (`actor_defeated`
		 * reads it). The world's for the checkpoint (ruling 4): a restore puts
		 * back what stood when the photograph was taken.
		 */
		actorHealth: z.record(actorIdSchema, nonNegativeIntegerSchema).readonly(),
		actorLocations: z.record(actorIdSchema, z.union([roomIdSchema, z.literal('offstage')])).readonly(),
		/*
		 * D234: the bodies the player has STRUCK, in first-strike order — the
		 * ledger `actor_attacked` reads, so a pack's `hostileWhen` can say "it
		 * turns on you when struck" (ruling 3). Append-only, and the world's:
		 * the checkpoint's return un-strikes what it puts back.
		 */
		attackedActorIds: z.array(actorIdSchema).readonly(),
		/*
		 * D244: what each counter holds, when an effect has moved it — a delta
		 * like `spentUses`: a counter absent from this record is at its
		 * declared `initial`, so a pack that grows a counter later needs
		 * nothing from a save already in the world. Every value lies inside
		 * its counter's range (the effects clamp). Whether the checkpoint
		 * rewinds an entry is the counter's `scope` to say, not this record's.
		 */
		counterValues: z.record(counterIdSchema, z.int()).readonly(),
		/**
		 * R25: where each currently-dropped item lies — a record rather than a
		 * set because a dropped thing is somewhere the author never placed it,
		 * and the room is the fact. An item leaves this record the moment it is
		 * taken again (Mike's rule: found by searching the room, until and
		 * unless re-taken).
		 */
		droppedItemLocations: z.record(itemIdSchema, roomIdSchema).readonly(),
		/**
		 * D143: what is worn or wielded, by slot — a record so a slot holds one
		 * thing and "already wearing" is a lookup. An equipped item is ALSO in
		 * `inventoryItemIds`: equipping is a fact about a carried thing.
		 */
		equippedItemIds: z.record(z.string(), itemIdSchema).readonly(),
		flagValues: z.record(flagIdSchema, z.boolean()).readonly(),
		inventoryItemIds: z.array(itemIdSchema).readonly(),
		/*
		 * D50, the container half of the "no derived data" rule. What a thing
		 * CONTAINS is authored and immutable, so it stays in the bundle; what the
		 * player has DONE to it is a delta and lives here. Two sets rather than a
		 * per-item record because both are sparse — most things are neither
		 * opened nor searched — and because `put X in Y`, when it arrives, is a
		 * third set rather than a reopening of this decision.
		 */
		openedContainerItemIds: z.array(itemIdSchema).readonly(),
		/*
		 * D234: what is left of the PLAYER's health, when that differs from the
		 * manifest's `player.health` — present only once a foe has drawn blood,
		 * absent in every pack that declares no body and in every world where
		 * the player is whole. A delta for the same reason the two records
		 * above are. Photographed with the world (ruling 4): a return to the
		 * checkpoint restores exactly the health the player had there.
		 */
		playerHealth: nonNegativeIntegerSchema.optional(),
		removedRoomItemIds: z.array(itemIdSchema).readonly(),
		/*
		 * D138: which hidden exits the world has REVEALED — the ledger that is
		 * the truth of an exit's visibility once its `reveal` fires, in exactly
		 * `unlockedExitIds`' shape and for exactly its reason: an exit the
		 * player has seen stays seen, whatever its condition does afterward.
		 * Append-only; written by the reveal watcher and nothing else.
		 */
		revealedExitIds: z.array(exitIdSchema).readonly(),
		/*
		 * D240: the BODIES the player has gone through, in first-search order —
		 * `searchedContainerItemIds`' twin for the things an actor carries
		 * (`containedIn.containerActorId`). A separate named set because an
		 * actor id is not an item id, and a save should say which it was. The
		 * world's, like the item set: the checkpoint's return rewinds a search.
		 */
		searchedActorIds: z.array(actorIdSchema).readonly(),
		searchedContainerItemIds: z.array(itemIdSchema).readonly(),
		/*
		 * D152: how many times each durable thing has been spent — a record
		 * keyed by item because most things are not durable and a spent count
		 * of zero is the same fact as no entry. Whether the checkpoint rewinds
		 * an entry is the item's `durability.scope` to say, not this record's.
		 */
		spentUses: z.record(itemIdSchema, nonNegativeIntegerSchema).readonly(),
		/*
		 * D56: which switches are ON right now — the same shape as
		 * `openedContainerItemIds`, seeded at genesis from declarations.
		 *
		 * Deliberately a SECOND named set rather than one generic map of binary
		 * properties: these names are self-documenting in a save file and a
		 * generic map is not, and the logic that moves them is shared in one
		 * place regardless. Shared behavior, legible data.
		 */
		switchedOnItemIds: z.array(itemIdSchema).readonly(),
		unlockedExitIds: z.array(exitIdSchema).readonly(),
	})
	.readonly();

/** The `world` sub-state — see {@link worldStateSchema}. */
export type WorldState = z.infer<typeof worldStateSchema>;
